import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import { AlertTriangle, CheckCircle, Edit3, XCircle } from "react-feather";
import StatusBadge from "../../../components/common/StatusBadge";
import InfoTile from "../../../components/common/InfoTile";
import AttachmentPreviewModal from "../../../components/common/AttachmentPreviewModal";
import {
  useCorrectFuelTransactionTypeMutation,
  useGetFuelLookupsQuery,
  useGetFuelTransactionQuery,
  useReviewFuelTransactionMutation,
  useVoidFuelTransactionMutation,
} from "../fuelApi";
import { useCanReviewFuelExceptions } from "../useFuelPermissions";
import {
  FUEL_EXCEPTION_LABEL,
  FUEL_SEVERITY,
  FUEL_TXN_STATUS_CLASS,
  FUEL_TXN_STATUS_LABEL,
  formatDateTime,
  kmSincePrevious,
  litres,
  money,
  whole,
} from "../fuelFormat";
import FuelEvidencePhoto from "./FuelEvidencePhoto";
import MismatchedCardBadge from "./MismatchedCardBadge";

const Fact = ({ label, children }) => (
  <>
    <dt className="col-5 text-muted fw-normal">{label}</dt>
    <dd className="col-7 mb-1">{children}</dd>
  </>
);

/**
 * One fuel purchase: every field, its fuel exceptions, and the two evidence
 * photos side by side. A holder of Fuel.ReviewExceptions can mark the entry as
 * reviewed (remarks required) or void it (reason required): voiding returns the
 * amount to the card and keeps the entry on record.
 *
 * The one field that can be changed is the fuel type (Batch211, 2026-10-15 -
 * "instead of petrol select diesel... when review doing can edit"). It moves no
 * money, so it is corrected in place with a reason instead of void + re-entry;
 * the fuel-type flags are re-checked server-side and every correction is listed
 * on the purchase. Litres, price, card or vehicle mistakes are still voided and
 * re-entered, because those move the card balance.
 */
const FuelTransactionDetailModal = ({ recordGuid, onClose }) => {
  const canReview = useCanReviewFuelExceptions();
  const { data, isLoading, isError, error } = useGetFuelTransactionQuery(recordGuid, { skip: !recordGuid });
  const [reviewTransaction, { isLoading: isReviewing }] = useReviewFuelTransactionMutation();
  const [voidTransaction, { isLoading: isVoiding }] = useVoidFuelTransactionMutation();
  const [correctFuelType, { isLoading: isCorrecting }] = useCorrectFuelTransactionTypeMutation();
  const { data: lookups } = useGetFuelLookupsQuery(undefined, { skip: !recordGuid || !canReview });
  const [action, setAction] = useState(null); // "review" | "void" | "fuelType"
  const [text, setText] = useState("");
  const [newFuelType, setNewFuelType] = useState("");
  const [preview, setPreview] = useState(null); // { blob, kind, label }

  useEffect(() => {
    setAction(null);
    setText("");
    setPreview(null);
  }, [recordGuid]);

  const t = data?.transaction;
  const exceptions = data?.exceptions || [];
  const corrections = data?.corrections || [];
  const isSaving = isReviewing || isVoiding || isCorrecting;
  const km = kmSincePrevious(t);
  const otherFuelTypes = (lookups?.fuelTypes || []).filter((ft) => ft !== t?.fuelType);

  const startAction = (next) => {
    setAction(next);
    setText("");
    setNewFuelType("");
  };

  const handleConfirm = async () => {
    try {
      if (action === "fuelType") {
        await correctFuelType({ recordGuid, fuelType: newFuelType, remarks: text.trim() }).unwrap();
        toast.success(`${t.transactionNumber} fuel type changed to ${newFuelType}`);
      } else if (action === "review") {
        await reviewTransaction({ recordGuid, reviewRemarks: text.trim() }).unwrap();
        toast.success(`${t.transactionNumber} marked as reviewed`);
      } else {
        await voidTransaction({ recordGuid, voidReason: text.trim() }).unwrap();
        toast.success(`${t.transactionNumber} voided - ${money(t.totalAmount)} returned to card ${t.cardNumber}`);
      }
      setAction(null);
      setText("");
    } catch (err) {
      toast.error(err?.message || "Could not save");
    }
  };

  return (
    <>
      <Modal show={Boolean(recordGuid)} onHide={isSaving ? undefined : onClose} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2 flex-wrap">
            {t ? (
              <>
                <span>{t.regNo}</span>
                <span className="text-muted fw-normal" style={{ fontSize: 14 }}>{t.transactionNumber} · {formatDateTime(t.transactionDate)}</span>
                <StatusBadge status={t.status} classMap={FUEL_TXN_STATUS_CLASS} labelMap={FUEL_TXN_STATUS_LABEL} />
              </>
            ) : (
              "Fuel purchase"
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading && <p className="text-muted mb-0">Loading…</p>}
          {isError && <div className="alert alert-danger mb-0">{error?.message || "Could not load this fuel purchase."}</div>}

          {t && (
            <>
              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3"><InfoTile label="Litres" value={litres(t.litres)} /></div>
                <div className="col-6 col-md-3"><InfoTile label="Price per litre" value={money(t.pricePerLitre)} /></div>
                <div className="col-6 col-md-3"><InfoTile label="Total amount" tone="maroon" value={money(t.totalAmount)} /></div>
                <div className="col-6 col-md-3">
                  <InfoTile label="Odometer">
                    {whole(t.odometerReading)} km
                    {km != null && <span className="text-muted ms-1 fw-normal" style={{ fontSize: 11 }}>(+{whole(km)} km)</span>}
                  </InfoTile>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <dl className="row mb-2" style={{ fontSize: 13 }}>
                    <Fact label="Vehicle">
                      <span className="fw-semibold">{t.regNo}</span> <span className="text-muted">{t.brand} {t.model}</span>
                    </Fact>
                    <Fact label="Fuel card">
                      <span className="fw-semibold">{t.cardNumber}</span> <span className="text-muted">{t.provider}</span>
                      {t.isCardMismatch && <div><MismatchedCardBadge expected={t.vehicleDefaultCardNumber} /></div>}
                    </Fact>
                    <Fact label="Card balance after">{money(t.cardBalanceAfter)}</Fact>
                    <Fact label="Fuel type">
                      {t.fuelType || "-"}
                      {corrections.map((c) => (
                        <div key={c.correctionId} className="text-muted" style={{ fontSize: 12 }}>
                          corrected from {c.oldValue} by {c.correctedByName || "-"} · {formatDateTime(c.correctedDate)}
                          <div>{c.remarks}</div>
                        </div>
                      ))}
                    </Fact>
                    <Fact label="Station">{t.stationName || "-"}</Fact>
                    <Fact label="Branch">{t.branchCode || "-"}</Fact>
                  </dl>
                </div>
                <div className="col-md-6">
                  <dl className="row mb-2" style={{ fontSize: 13 }}>
                    <Fact label="Recorded by">{t.recordedByName || "-"}</Fact>
                    <Fact label="Date / time (server)">{formatDateTime(t.transactionDate)}</Fact>
                    <Fact label="Previous odometer">{t.previousOdometerReading == null ? "-" : `${whole(t.previousOdometerReading)} km`}</Fact>
                    <Fact label="Remarks">{t.remarks || "-"}</Fact>
                    {t.reviewedDate && (
                      <Fact label="Reviewed">
                        {t.reviewedByName || "-"} · {formatDateTime(t.reviewedDate)}
                        {t.reviewRemarks && <div className="text-muted">{t.reviewRemarks}</div>}
                      </Fact>
                    )}
                    {t.voidedDate && (
                      <Fact label="Voided">
                        {t.voidedByName || "-"} · {formatDateTime(t.voidedDate)}
                        {t.voidReason && <div className="text-muted">{t.voidReason}</div>}
                      </Fact>
                    )}
                  </dl>
                </div>
              </div>

              <h6 className="mt-2 mb-2 d-flex align-items-center gap-2">
                <AlertTriangle size={15} /> Fuel exceptions
                <span className="text-muted fw-normal small">{exceptions.length === 0 ? "none" : `(${exceptions.length})`}</span>
              </h6>
              {exceptions.length === 0 ? (
                <p className="text-muted small">Nothing was flagged on this purchase.</p>
              ) : (
                <ul className="list-unstyled mb-3">
                  {exceptions.map((ex) => {
                    const severity = FUEL_SEVERITY[ex.severity] || FUEL_SEVERITY.WARNING;
                    return (
                      <li key={ex.exceptionId} className="d-flex align-items-start gap-2 border rounded px-2 py-1 mb-1" style={{ fontSize: 13 }}>
                        <span className="badge flex-shrink-0 mt-1" style={severity.style}>{severity.label}</span>
                        <span>
                          <span className="fw-semibold">{FUEL_EXCEPTION_LABEL[ex.exceptionCode] || ex.exceptionCode}</span>
                          <div className="text-muted">{ex.message}</div>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}

              <h6 className="mt-3 mb-2">Evidence photos</h6>
              <div className="d-flex flex-wrap justify-content-between gap-2">
                <FuelEvidencePhoto
                  recordGuid={t.recordGuid}
                  kind="odometer"
                  label="Odometer"
                  caption={`entered: ${whole(t.odometerReading)} km`}
                  available={t.hasOdometerImage}
                  onOpen={(blob, kind, label) => setPreview({ blob, kind, label })}
                />
                <FuelEvidencePhoto
                  recordGuid={t.recordGuid}
                  kind="pump"
                  label="Pump / receipt"
                  caption={`entered: ${litres(t.litres)} L · ${money(t.totalAmount)}`}
                  available={t.hasPumpImage}
                  onOpen={(blob, kind, label) => setPreview({ blob, kind, label })}
                />
              </div>
              <small className="text-muted d-block mt-2">
                Camera photos; the server&apos;s time is stamped onto each one when it is uploaded. Click a photo to open it full size.
              </small>

              {canReview && action && (
                <div className="card mt-3 mb-0" style={{ borderColor: action === "void" ? "#dc3545" : "#D0D5DD" }}>
                  <div className="card-body">
                    {action === "fuelType" ? (
                      <>
                        <p className="small text-muted mb-2">
                          Changes only the fuel type - litres, amount and the card balance stay as they are. The fuel-type
                          checks are run again, so a flag that no longer applies is cleared. The change and your reason are
                          kept on this purchase.
                        </p>
                        <label className="form-label">
                          Correct fuel type <span className="text-danger">*</span>
                        </label>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          {otherFuelTypes.map((ft) => (
                            <button
                              key={ft}
                              type="button"
                              className={`btn btn-sm ${newFuelType === ft ? "btn-primary" : "btn-outline-secondary"}`}
                              onClick={() => setNewFuelType(ft)}
                            >
                              {ft}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : action === "void" ? (
                      <div className="alert alert-warning py-2" style={{ fontSize: 13 }}>
                        Voiding returns <b>{money(t.totalAmount)}</b> to card <b>{t.cardNumber}</b> and leaves this entry out of
                        the reports. The entry itself, its photos and this reason <b>stay on record</b> - nothing is deleted,
                        and a voided entry cannot be restored. If the purchase really happened, it has to be entered again.
                      </div>
                    ) : (
                      <p className="small text-muted mb-2">
                        Marks this purchase as looked at. It does not change the amount or the card balance.
                      </p>
                    )}
                    <label className="form-label">
                      {action === "void" ? "Reason for voiding" : action === "fuelType" ? "Reason for the change" : "Review remarks"}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    <textarea className="form-control mb-2" rows={2} value={text} onChange={(e) => setText(e.target.value)} />
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className={`btn btn-${action === "void" ? "danger" : action === "fuelType" ? "primary" : "success"}`}
                        disabled={!text.trim() || isSaving || (action === "fuelType" && !newFuelType)}
                        onClick={handleConfirm}
                      >
                        {isSaving
                          ? "Saving…"
                          : action === "void"
                            ? "Yes, void this entry"
                            : action === "fuelType"
                              ? `Change to ${newFuelType || "…"}`
                              : "Mark as reviewed"}
                      </button>
                      <button type="button" className="btn btn-secondary" disabled={isSaving} onClick={() => setAction(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {t && canReview && !action && t.status !== "VOIDED" && (
            <>
              {t.status !== "REVIEWED" && (
                <Button variant="success" className="d-inline-flex align-items-center gap-2" onClick={() => startAction("review")}>
                  <CheckCircle size={16} /> Mark as reviewed
                </Button>
              )}
              {otherFuelTypes.length > 0 && (
                <Button variant="outline-primary" className="d-inline-flex align-items-center gap-2" onClick={() => startAction("fuelType")}>
                  <Edit3 size={16} /> Correct fuel type
                </Button>
              )}
              <Button variant="outline-danger" className="d-inline-flex align-items-center gap-2 me-auto" onClick={() => startAction("void")}>
                <XCircle size={16} /> Void entry
              </Button>
            </>
          )}
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <AttachmentPreviewModal
        show={Boolean(preview)}
        onClose={() => setPreview(null)}
        blob={preview?.blob}
        fileName={t && preview ? `${t.transactionNumber}-${preview.kind}.jpg` : undefined}
        title={t && preview ? `${t.regNo} - ${preview.label}` : ""}
      />
    </>
  );
};

export default FuelTransactionDetailModal;
